import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPosts } from '../../services/postsService';
import { fetchPostLikeSummary, removePostLike, upsertPostLike } from '../../services/postLikesService';
import { isSupabaseConfigured } from '../../lib/supabase';
import { useTabVisibility } from '../../hooks/useTabVisibility';
import { usePageMeta } from '../../hooks/usePageMeta';


const fallbackPosts = [
  {
    id: 'demo-1',
    title: 'RCC Recruitment',
    content: 'Halina\'t sumali sa team at sabayan kami sa aming mga weekend long rides at races.',
    category: 'Announcements',
    status: 'Published',
    featured_image: '/latest_post.jpg',
    created_at: '2025-12-01T00:00:00.000Z',
  },
  {
    id: 'demo-2',
    title: 'Larga Pilipinas Championship',
    content: 'Congratulations sa buong team para sa panibagong podium finish!',
    category: 'Race Reports',
    status: 'Published',
    featured_image: '/upcoming_events.jpg',
    created_at: '2025-11-20T00:00:00.000Z',
  },
];

const MAX_PREVIEW_LENGTH = 150;

const categoryThemes = {
  Announcements: {
    badge: 'bg-reptilez-green-100 text-reptilez-green-700 border border-reptilez-green-300',
    avatar: 'bg-reptilez-green-50 text-reptilez-green-700 border border-reptilez-green-300',
  },
  'Race Reports': {
    badge: 'bg-red-100 text-red-700 border border-red-300',
    avatar: 'bg-red-50 text-red-700 border border-red-300',
  },
  default: {
    badge: 'bg-reptilez-green-100 text-reptilez-green-700 border border-reptilez-green-300',
    avatar: 'bg-reptilez-green-50 text-reptilez-green-700 border border-reptilez-green-300',
  },
};

const getStoredLikes = () => {
  try {
    const stored = localStorage.getItem('rcc-post-likes');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Unable to parse stored likes', error);
    return {};
  }
};

const supabaseReady = isSupabaseConfigured;

const ViewPost = ({ singleView = false }) => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [expandedPosts, setExpandedPosts] = useState({});
  const [likedPosts, setLikedPosts] = useState(() => (supabaseReady ? {} : getStoredLikes()));
  const [likeCounts, setLikeCounts] = useState({});
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [activeMedia, setActiveMedia] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [allMediaForViewer, setAllMediaForViewer] = useState([]);
  const [shareState, setShareState] = useState({ postId: null, message: '' });

  usePageMeta('Posts');

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await getPosts();
      if (Array.isArray(data) && data.length > 0) {
        setPosts(data);
        
        // If single view and post not found, redirect to posts page
        if (singleView && postId) {
          const postExists = data.some(p => p.id === postId);
          if (!postExists) {
            navigate('/posts');
          }
        }
      } else {
        setPosts(fallbackPosts);
      }
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Unable to load posts from Supabase. Showing sample data for now.');
      setPosts(fallbackPosts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh when tab becomes visible
  useTabVisibility(fetchPosts);

  useEffect(() => {
    if (!supabaseReady) {
      localStorage.setItem('rcc-post-likes', JSON.stringify(likedPosts));
    }
  }, [likedPosts]);

  useEffect(() => {
    if (supabaseReady || Object.keys(likedPosts).length === 0) return;
    setLikeCounts(
      Object.keys(likedPosts).reduce((acc, postId) => {
        acc[postId] = 1;
        return acc;
      }, {})
    );
  }, [likedPosts]);

  useEffect(() => {
    const initFingerprint = async () => {
      try {
        let deviceId = localStorage.getItem('rcc-device-id');
        if (!deviceId) {
          deviceId =
            (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
            `device-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
          localStorage.setItem('rcc-device-id', deviceId);
        }
        const model = navigator.userAgent || 'Unknown Device';
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown Region';
        const location = timezone;

        setDeviceFingerprint({
          id: deviceId,
          model,
          location,
        });
      } catch (fingerprintError) {
        console.error('Failed to initialize fingerprint', fingerprintError);
        setDeviceFingerprint({
          id: 'unknown',
          model: 'Unknown Device',
          location: 'Unknown Region',
        });
      }
    };

    initFingerprint();
  }, []);

  const filteredPosts = useMemo(() => {
    // If single view mode, only show the specific post
    if (singleView && postId) {
      return posts.filter(post => post.id === postId);
    }
    
    // Otherwise, apply search filter
    return posts.filter((post) =>
      post.title?.toLowerCase().includes(search.toLowerCase()) ||
      post.content?.toLowerCase().includes(search.toLowerCase())
    );
  }, [posts, search, singleView, postId]);

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date)) return '';
    const datePart = date.toLocaleDateString('en-US', {
      timeZone: 'Asia/Manila',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = date
      .toLocaleTimeString('en-US', {
        timeZone: 'Asia/Manila',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();
    return `${datePart} at ${timePart}`;
  };

  const getCategoryTheme = (category) => {
    if (!category) return categoryThemes.default;
    return categoryThemes[category] || categoryThemes.default;
  };

  useEffect(() => {
    if (!supabaseReady || !deviceFingerprint?.id) return;
    let isMounted = true;
    const loadSupabaseLikes = async () => {
      try {
        const { counts, likedPostIds } = await fetchPostLikeSummary(deviceFingerprint.id);
        if (!isMounted) return;
        setLikeCounts(counts);
        setLikedPosts(
          likedPostIds.reduce((acc, id) => {
            acc[id] = true;
            return acc;
          }, {})
        );
      } catch (likeError) {
        console.error('Failed to load like summary', likeError);
      }
    };
    loadSupabaseLikes();
    return () => {
      isMounted = false;
    };
  }, [deviceFingerprint]);

  const nextLikeValue = (current = 0, liked) => {
    if (liked) {
      return Math.max(0, current - 1);
    }
    return current + 1;
  };

  const heartCopy = (count, liked) => {
    if (count > 0) {
      if (liked) {
        return count === 1 ? 'You love this' : `You and ${count - 1} rider${count - 1 === 1 ? '' : 's'} love this`;
      }
      return `${count} rider${count === 1 ? '' : 's'} love this`;
    }
    return 'Be the first to send hearts';
  };

  const toggleLike = async (postId) => {
    if (!deviceFingerprint?.id) return;
    const alreadyLiked = !!likedPosts[postId];

    // Optimistic UI update
    setLikedPosts((prev) => {
      const next = { ...prev };
      if (alreadyLiked) {
        delete next[postId];
      } else {
        next[postId] = true;
      }
      return next;
    });
    setLikeCounts((prev) => ({
      ...prev,
      [postId]: nextLikeValue(prev[postId], alreadyLiked),
    }));

    if (!supabaseReady) return;

    try {
      if (alreadyLiked) {
        await removePostLike(postId, deviceFingerprint.id);
      } else {
        await upsertPostLike(postId, deviceFingerprint);
      }
    } catch (likeError) {
      console.error('Unable to update like in Supabase', likeError);

      // Rollback optimistic update on error
      setLikedPosts((prev) => {
        const next = { ...prev };
        if (alreadyLiked) {
          next[postId] = true;
        } else {
          delete next[postId];
        }
        return next;
      });
      setLikeCounts((prev) => ({
        ...prev,
        [postId]: nextLikeValue(prev[postId], !alreadyLiked),
      }));

      setShareState({ postId, message: 'Unable to update hearts right now.' });
      setTimeout(() => {
        setShareState((prev) => (prev.postId === postId ? { postId: null, message: '' } : prev));
      }, 2500);
    }
  };

  const handleShare = async (post) => {
    const sharePayload = {
      title: post.title,
      text: post.content,
      url: `${window.location.origin}/posts/${post.id}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        setShareState({ postId: post.id, message: '✓ Shared successfully!' });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(sharePayload.url);
        setShareState({ postId: post.id, message: '✓ Link copied to clipboard!' });
      } else {
        setShareState({ postId: post.id, message: 'Sharing not supported on this device.' });
      }
    } catch (shareError) {
      if (shareError.name === 'AbortError') {
        // User cancelled - don't show error
        return;
      }
      console.error('Share failed', shareError);
      setShareState({ postId: post.id, message: '✗ Share failed. Please try again.' });
    } finally {
      setTimeout(() => {
        setShareState((prev) => (prev.postId === post.id ? { postId: null, message: '' } : prev));
      }, 2500);
    }
  };

  const openMediaViewer = (mediaArray, startIndex, postTitle, postCategory) => {
    setAllMediaForViewer(mediaArray.filter(m => m.type === 'image'));
    setCurrentMediaIndex(startIndex);
    setActiveMedia({
      src: mediaArray[startIndex].url,
      title: postTitle,
      meta: postCategory,
    });
  };

  const handleNextMedia = () => {
    if (currentMediaIndex < allMediaForViewer.length - 1) {
      const nextIndex = currentMediaIndex + 1;
      setCurrentMediaIndex(nextIndex);
      setActiveMedia(prev => ({
        ...prev,
        src: allMediaForViewer[nextIndex].url,
      }));
    }
  };

  const handlePrevMedia = () => {
    if (currentMediaIndex > 0) {
      const prevIndex = currentMediaIndex - 1;
      setCurrentMediaIndex(prevIndex);
      setActiveMedia(prev => ({
        ...prev,
        src: allMediaForViewer[prevIndex].url,
      }));
    }
  };

  useEffect(() => {
    if (!activeMedia) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveMedia(null);
      } else if (e.key === 'ArrowRight') {
        handleNextMedia();
      } else if (e.key === 'ArrowLeft') {
        handlePrevMedia();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMedia, currentMediaIndex, allMediaForViewer]);

  return (
    <div
      className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-gradient-to-br from-reptilez-white-50 via-reptilez-white-100 to-reptilez-green-50"
    >
      <div className="layout-container flex grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 flex flex-1 justify-center py-2">
          <div className="layout-content-container flex flex-col w-full max-w-[960px] flex-1 pt-3 md:pt-2 pb-6 sm:pb-8">
            <section className="space-y-2 sm:space-y-3">
              {/* Only show search bar if not in single view mode */}
              {!singleView && (
                <label className="flex min-w-0 sm:min-w-40 h-10 sm:h-12 w-full rounded-full border border-reptilez-green-200 bg-white px-3 sm:px-4 text-gray-900 backdrop-blur focus-within:border-reptilez-green-600 focus-within:ring-2 focus-within:ring-reptilez-green-500/30 shadow-sm">
                  <span className="flex items-center pr-2 sm:pr-3 text-reptilez-green-600">
                    <span className="material-symbols-outlined text-sm sm:text-base">search</span>
                  </span>
                  <input
                    className="w-full flex-1 bg-transparent text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                    placeholder="Search posts..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
              )}
              
              {/* Back button for single view */}
              {singleView && (
                <button
                  onClick={() => navigate('/posts')}
                  className="flex items-center gap-2 text-reptilez-green-700 hover:text-reptilez-green-800 transition-colors group"
                >
                  <span className="material-symbols-outlined text-xl transition-transform group-hover:-translate-x-1">arrow_back</span>
                  <span className="font-semibold">Back to all posts</span>
                </button>
              )}
              
              {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {error}
                </div>
              )}
            </section>
            <div className="mt-6">
              {loading ? (
                <div className="space-y-4 pb-12">
                  {[1, 2, 3].map((skeleton) => (
                    <div
                      key={skeleton}
                      className="overflow-hidden rounded-3xl border border-reptilez-green-200 bg-white p-5 md:p-6 space-y-4 shadow-sm"
                    >
                      <div className="flex items-center gap-4">
                        <div className="size-12 rounded-full shimmer-bg" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-40 rounded-full shimmer-bg" />
                          <div className="h-2 w-24 rounded-full shimmer-bg" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 w-3/4 rounded-full shimmer-bg" />
                        <div className="h-3 w-full rounded-full shimmer-bg" />
                        <div className="h-3 w-5/6 rounded-full shimmer-bg" />
                      </div>
                      <div className="h-40 w-full rounded-2xl shimmer-bg" />
                    </div>
                  ))}
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="flex min-h-[280px] items-center justify-center text-gray-600 bg-white rounded-xl border border-reptilez-green-100 p-8">
                  No posts matched your search.
                </div>
              ) : (
                <div className="space-y-6 pb-12">
                  {filteredPosts.map((post) => (
                    <article
                      key={post.id}
                      className="overflow-hidden rounded-3xl border border-reptilez-green-200 bg-white shadow-md backdrop-blur-md"
                    >
                      <div className="space-y-4 p-5 md:p-6">
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="relative">
                            <div className="flex size-12 items-center justify-center rounded-full border-2 border-reptilez-green-300 bg-reptilez-green-50 p-[3px]">
                              <img
                                src="/rcc1.png"
                                alt="Reptilez Cycling Club avatar"
                                className="h-10 w-10 rounded-full object-contain"
                              />
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-base font-semibold text-gray-900">
                              {post.author_name || 'Reptilez Cycling Club'}
                            </p>
                            <p className="text-xs text-gray-600">{formatDate(post.created_at)}</p>
                          </div>
                          <span
                            className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getCategoryTheme(post.category).badge}`}
                          >
                            {post.category || 'Updates'}
                          </span>
                        </div>
                        <div className="space-y-3">
                          <h3 className="text-2xl font-bold text-gray-900 leading-snug">{post.title}</h3>
                          <p className="whitespace-pre-line text-gray-700 text-sm leading-relaxed break-words">
                            {(() => {
                              const content = post.content || '';
                              const isExpanded = expandedPosts[post.id];
                              const shouldTruncate = content.length > MAX_PREVIEW_LENGTH;
                              if (!shouldTruncate || isExpanded) {
                                return content;
                              }
                              return `${content.substring(0, MAX_PREVIEW_LENGTH)}...`;
                            })()}
                          </p>
                          {(post.content || '').length > MAX_PREVIEW_LENGTH && (
                            <button
                              type="button"
                              className="text-reptilez-green-700 font-semibold text-xs hover:text-reptilez-green-800 transition-colors"
                              onClick={() =>
                                setExpandedPosts((prev) => ({
                                  ...prev,
                                  [post.id]: !prev[post.id],
                                }))
                              }
                            >
                              {expandedPosts[post.id] ? 'See less' : 'See more'}
                            </button>
                          )}
                        </div>
                      </div>
                      {(() => {
                        const media = post.media && Array.isArray(post.media) && post.media.length > 0
                          ? post.media
                          : (post.featured_image ? [{ url: post.featured_image, type: 'image' }] : []);

                        if (media.length === 0) return null;

                        // Single media
                        if (media.length === 1) {
                          const item = media[0];
                          return (
                            <div className="relative flex w-full overflow-hidden border-y border-reptilez-green-100">
                              {item.type === 'video' ? (
                                <video
                                  className="w-full aspect-video object-cover"
                                  src={item.url}
                                  controls
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="group relative flex w-full overflow-hidden focus:outline-none"
                                  onClick={() => openMediaViewer(media, 0, post.title, post.category)}
                                  aria-label={`View full image for ${post.title}`}
                                >
                                  <div
                                    className="aspect-video w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.02]"
                                    style={{ backgroundImage: `url("${item.url}")` }}
                                    role="img"
                                    aria-label={post.title}
                                  />
                                </button>
                              )}
                            </div>
                          );
                        }

                        // Multiple media - Facebook-style grid (max 5 shown)
                        const displayCount = Math.min(media.length, 5);
                        const remainingCount = media.length > 5 ? media.length - 5 : 0;

                        // Grid layout logic
                        let gridClass = '';
                        let gridItems = [];

                        if (displayCount === 2) {
                          gridClass = 'grid grid-cols-2 gap-1';
                          gridItems = media.slice(0, 2).map((item, idx) => ({
                            item,
                            idx,
                            className: 'aspect-square'
                          }));
                        } else if (displayCount === 3) {
                          // For 3 images: First 2 images on top row, 1 large image below spanning full width
                          gridItems = [
                            { item: media[0], idx: 0, className: 'aspect-square' },
                            { item: media[1], idx: 1, className: 'aspect-square' },
                            { item: media[2], idx: 2, className: 'col-span-2 aspect-video' }
                          ];
                        } else if (displayCount === 4) {
                          gridClass = 'grid grid-cols-2 gap-1';
                          gridItems = media.slice(0, 4).map((item, idx) => ({
                            item,
                            idx,
                            className: 'aspect-square'
                          }));
                        } else if (displayCount >= 5) {
                          // For 5+ images: 2 large images on top (taller), 3 smaller below
                          gridItems = [
                            { item: media[0], idx: 0, className: 'col-span-1', isLarge: true },
                            { item: media[1], idx: 1, className: 'col-span-1', isLarge: true },
                            { item: media[2], idx: 2, className: 'aspect-square', isLarge: false },
                            { item: media[3], idx: 3, className: 'aspect-square', isLarge: false },
                            { item: media[4], idx: 4, className: 'aspect-square', isLarge: false }
                          ];
                        }

                        return (
                          <div className="border-y border-white/5 bg-black/20 p-1">
                            {displayCount >= 5 ? (
                              // Special layout for 5+ images
                              <div className="flex flex-col gap-1">
                                {/* Top row - 2 large images */}
                                <div className="grid grid-cols-2 gap-1">
                                  {gridItems.slice(0, 2).map(({ item, idx, className }) => (
                                    <div key={idx} className="relative overflow-hidden" style={{ aspectRatio: '1/1.2' }}>
                                      {item.type === 'video' ? (
                                        <video
                                          className="w-full h-full object-cover cursor-pointer"
                                          src={item.url}
                                          controls
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      ) : (
                                        <button
                                          type="button"
                                          className="group relative w-full h-full focus:outline-none"
                                          onClick={() => openMediaViewer(media, idx, post.title, post.category)}
                                        >
                                          <div
                                            className="w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                                            style={{ backgroundImage: `url("${item.url}")` }}
                                          />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {/* Bottom row - 3 smaller images */}
                                <div className="grid grid-cols-3 gap-1">
                                  {gridItems.slice(2, 5).map(({ item, idx, className }) => (
                                    <div key={idx} className={`relative overflow-hidden ${className}`}>
                                      {item.type === 'video' ? (
                                        <video
                                          className="w-full h-full object-cover cursor-pointer"
                                          src={item.url}
                                          controls
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      ) : (
                                        <button
                                          type="button"
                                          className="group relative w-full h-full focus:outline-none"
                                          onClick={() => openMediaViewer(media, idx, post.title, post.category)}
                                        >
                                          <div
                                            className="w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                                            style={{ backgroundImage: `url("${item.url}")` }}
                                          />
                                          {remainingCount > 0 && idx === 4 && (
                                            <div className="absolute inset-0 bg-black/70 flex items-center justify-center pointer-events-none">
                                              <span className="text-white text-4xl font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">+{remainingCount}</span>
                                            </div>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              // Original layout for 2-4 images
                              <div className={displayCount === 3 ? 'grid grid-cols-2 gap-1' : gridClass}>
                                {gridItems.map(({ item, idx, className }) => (
                                <div key={idx} className={`relative overflow-hidden ${className}`}>
                                  {item.type === 'video' ? (
                                    <video
                                      className="w-full h-full object-cover cursor-pointer"
                                      src={item.url}
                                      controls
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className="group relative w-full h-full focus:outline-none"
                                      onClick={() => openMediaViewer(media, idx, post.title, post.category)}
                                    >
                                      <div
                                        className="w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                                        style={{ backgroundImage: `url("${item.url}")` }}
                                      />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white px-5 py-3 text-xs text-gray-700 md:px-6">
                        <span className="flex items-center gap-2">
                          <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
                            <span
                              className="material-symbols-outlined text-base text-red-600"
                              style={likedPosts[post.id] ? { fontVariationSettings: "'FILL' 1" } : {}}
                            >
                              favorite
                            </span>
                            <span className="text-xs font-semibold text-gray-900">
                              {likeCounts[post.id] ?? 0}
                            </span>
                          </span>
                          <span>{heartCopy(likeCounts[post.id] ?? 0, !!likedPosts[post.id])}</span>
                        </span>
                      </div>
                      <div className="flex divide-x divide-gray-200 border-t border-gray-200 bg-white">
                        {[
                          { label: likedPosts[post.id] ? 'Liked' : 'Heart', icon: 'favorite', onClick: () => toggleLike(post.id), active: likedPosts[post.id] },
                          { label: 'Share', icon: 'ios_share', onClick: () => handleShare(post), active: false },
                        ].map((action) => (
                          <button
                            key={action.label}
                            type="button"
                            onClick={action.onClick}
                            className={`flex flex-1 items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-gray-50 ${
                              action.active && action.icon === 'favorite' ? 'text-red-600' : 'text-gray-700'
                            }`}
                          >
                            <span
                              className={`material-symbols-outlined text-base ${
                                action.icon === 'favorite' && action.active ? 'text-red-600' : ''
                              }`}
                              style={action.icon === 'favorite' && action.active ? { fontVariationSettings: "'FILL' 1" } : {}}
                            >
                              {action.icon}
                            </span>
                            {action.label}
                          </button>
                        ))}
                      </div>
                      {shareState.postId === post.id && shareState.message && (
                        <div className="border-t border-gray-200 bg-white px-5 py-3 text-sm text-center">
                          <span className={shareState.message.startsWith('✓') ? 'text-gray-700' : 'text-red-600'}>
                            {shareState.message}
                          </span>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {activeMedia && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
          {/* Close Button */}
          <button
            type="button"
            className="absolute right-6 top-6 z-10 rounded-full border border-reptilez-green-200 bg-white/90 p-3 text-gray-700 hover:bg-reptilez-green-600 hover:text-white focus:outline-none transition-all duration-300 hover:scale-110 shadow-lg"
            onClick={() => setActiveMedia(null)}
            aria-label="Close media viewer"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>

          {/* Previous Button */}
          {currentMediaIndex > 0 && (
            <button
              type="button"
              className="absolute left-6 z-10 rounded-full border border-reptilez-green-200 bg-white/90 p-3 text-gray-700 hover:bg-reptilez-green-600 hover:text-white focus:outline-none transition-all duration-300 hover:scale-110 shadow-lg"
              onClick={handlePrevMedia}
              aria-label="Previous image"
            >
              <span className="material-symbols-outlined text-2xl">chevron_left</span>
            </button>
          )}

          {/* Next Button */}
          {currentMediaIndex < allMediaForViewer.length - 1 && (
            <button
              type="button"
              className="absolute right-6 z-10 rounded-full border border-reptilez-green-200 bg-white/90 p-3 text-gray-700 hover:bg-reptilez-green-600 hover:text-white focus:outline-none transition-all duration-300 hover:scale-110 shadow-lg"
              onClick={handleNextMedia}
              aria-label="Next image"
            >
              <span className="material-symbols-outlined text-2xl">chevron_right</span>
            </button>
          )}

          {/* Image Container */}
          <div className="w-full max-w-5xl overflow-hidden rounded-3xl border border-reptilez-green-200 bg-white shadow-2xl">
            <div className="flex max-h-[80vh] items-center justify-center bg-reptilez-green-50">
              <img
                src={activeMedia.src}
                alt={activeMedia.title}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />
            </div>
            <div className="border-t border-reptilez-green-200 px-6 py-4 text-center text-gray-700 bg-white">
              <p className="text-lg font-semibold">{activeMedia.title}</p>
              <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{activeMedia.meta}</p>
              <p className="text-sm text-gray-600 mt-2">
                {currentMediaIndex + 1} / {allMediaForViewer.length}
              </p>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        .shimmer-bg {
          background: linear-gradient(
            90deg,
            rgba(250, 250, 250, 1) 0%,
            rgba(34, 197, 94, 0.1) 50%,
            rgba(250, 250, 250, 1) 100%
          );
          background-size: 1000px 100%;
          animation: shimmer 2s infinite linear;
        }
      `}</style>
    </div>
  );
};

export default ViewPost;